// Typed wrappers for the micro-system flow (decision D28, docs/delivery/microsystem-plan.md §2 — fixed HTTP
// contracts): create a project from a brief, publish it to Coolify, and read the project's agent runs. The API enforces
// every permission (from-brief and publish: organization admin; reads: project observer); the UI only avoids dead ends.
//
// TODO(D28): import ProjectPublish, FromBriefRequest and FromBriefResult from @maestros/contracts once
// packages/contracts/src/microsystem.ts lands (written in parallel by another work package); until then these local
// types mirror the plan's §2 exactly and must not drift from it.
import type { AgentRun, AgentRunOutcome, AgentRunPurpose, Project, RepositoryProvision } from "@maestros/contracts";
import { api } from "./client.ts";

const enc = encodeURIComponent;

/** `creating_repository` (D33): a project without a GitHub repository gets a private one created and mapped first. */
export const PUBLISH_STATES = ["queued", "creating_repository", "pushing", "creating_app", "deploying", "succeeded", "failed"] as const;
export type PublishState = (typeof PUBLISH_STATES)[number];
/** States after which nothing changes any more: the card stops polling. */
export const FINAL_PUBLISH_STATES: readonly PublishState[] = ["succeeded", "failed"];

/** `POST /v1/projects/from-brief` body (plan §2). */
export interface FromBriefRequest {
  name: string;
  brief: string;
  integration_id?: string;
}

/** `POST /v1/projects/from-brief` 201 (plan §2). `project` is a ProjectDetail; only the Project fields are read here. */
export interface FromBriefResult {
  project: Project;
  repository: { full_name: string; html_url: string; private: true };
  provision: RepositoryProvision;
  prd_version_id: string;
  planning_run_id: string | null;
}

/** One publish of a project to Coolify (plan §2). */
export interface ProjectPublish {
  id: string;
  project_id: string;
  state: PublishState;
  /** "owner/name" */
  repository: string | null;
  commit_sha: string | null;
  coolify_app_uuid: string | null;
  deployment_uuid: string | null;
  app_url: string | null;
  error_code: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

/** Creates the project, its private repository, provision and planning run (organization admin; 409 `github_unavailable` / `repository_exists`). */
export const createProjectFromBrief = (body: FromBriefRequest, idempotencyKey: string) =>
  api<FromBriefResult>("/projects/from-brief", { method: "POST", body, idempotencyKey });

/** Starts a publish (202), or returns the one already in flight (200). Organization admin. */
export const publishProject = (projectId: string) => api<{ publish: ProjectPublish }>(`/projects/${enc(projectId)}/publish`, { method: "POST", body: {} });

/** The project's latest publish, or null when it was never published. Project observer. */
export const getProjectPublish = (projectId: string, signal?: AbortSignal) =>
  api<{ publish: ProjectPublish | null }>(`/projects/${enc(projectId)}/publish`, signal ? { signal } : {});

export interface AgentRunFilters {
  project_id?: string;
  task_id?: string;
  purpose?: AgentRunPurpose;
  outcome?: AgentRunOutcome;
  limit?: number;
}

/** `GET /v1/agent-runs`, newest first (apps/api/src/routes/agents.ts; `project_id` needs observer on that project). */
export const listAgentRuns = (filters: AgentRunFilters, signal?: AbortSignal) =>
  api<{ items: AgentRun[] }>("/agent-runs", { query: { ...filters }, ...(signal ? { signal } : {}) });
